import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileText, AlertTriangle, Shield, Search, X } from 'lucide-react';
import { useSectorTerms } from '@/utils/useSectorTerms';

// Static map for action codes. The `customer.deleted` label is intentionally
// generic here — the actual label is composed dynamically inside the component
// via the `useSectorTerms()` hook so apartment admins see "Sakin silindi",
// gym admins see "Üye silindi", clinics see "Hasta silindi", etc.
const ACTION_LABELS = {
  'auth.login_success': { label: 'Giriş başarılı', color: 'text-emerald-700 bg-emerald-50' },
  'auth.login_failed': { label: 'Giriş başarısız', color: 'text-red-700 bg-red-50' },
  'auth.logout': { label: 'Çıkış', color: 'text-slate-700 bg-slate-50' },
  'auth.superadmin.login_success': { label: 'SA Giriş', color: 'text-emerald-700 bg-emerald-50' },
  'auth.superadmin.login_failed': { label: 'SA Giriş BAŞARISIZ', color: 'text-red-700 bg-red-50' },
  'auth.superadmin.2fa_enabled': { label: 'SA 2FA açıldı', color: 'text-blue-700 bg-blue-50' },
  'auth.superadmin.2fa_disabled': { label: 'SA 2FA kapatıldı', color: 'text-amber-700 bg-amber-50' },
  'auth.superadmin.recovery_code_used': { label: 'SA yedek kod kullanıldı', color: 'text-amber-700 bg-amber-50' },
  'auth.superadmin.recovery_codes_regenerated': { label: 'SA yedek kodlar yenilendi', color: 'text-blue-700 bg-blue-50' },
  'superadmin.business.frozen': { label: 'İşletme donduruldu', color: 'text-cyan-700 bg-cyan-50' },
  'superadmin.business.unfrozen': { label: 'İşletme aktifleştirildi', color: 'text-emerald-700 bg-emerald-50' },
  'superadmin.business.deleted': { label: 'İşletme silindi', color: 'text-red-700 bg-red-50' },
  'superadmin.plan.changed': { label: 'SA plan değiştirdi', color: 'text-purple-700 bg-purple-50' },
  'superadmin.sms.toppedup': { label: 'SA SMS yükledi', color: 'text-blue-700 bg-blue-50' },
  'superadmin.fee.changed': { label: 'SA ücret değiştirdi', color: 'text-violet-700 bg-violet-50' },
  'business.plan.upgraded': { label: 'Plan yükseltildi', color: 'text-purple-700 bg-purple-50' },
  'customer.deleted': { label: 'Müşteri silindi', color: 'text-orange-700 bg-orange-50' },
  'payment.deleted': { label: 'Ödeme silindi', color: 'text-orange-700 bg-orange-50' },
  'branch.deleted': { label: 'Şube silindi', color: 'text-orange-700 bg-orange-50' },
};

const formatAction = (action, terms) => {
  if (action === 'customer.deleted') {
    return { label: `${terms.customerLabelSingular} silindi`, color: 'text-orange-700 bg-orange-50' };
  }
  return ACTION_LABELS[action] || { label: action, color: 'text-slate-700 bg-slate-100' };
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const DiffCell = ({ before, after }) => {
  if (!before && !after) return <span className="text-[#999]">-</span>;
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  return (
    <div className="text-xs space-y-0.5">
      {[...keys].map((k) => {
        const b = before?.[k];
        const a = after?.[k];
        if (JSON.stringify(b) === JSON.stringify(a)) return null;
        return (
          <div key={k}>
            <span className="font-semibold text-[#1A1A2E]">{k}:</span>{' '}
            <span className="text-red-600 line-through">{JSON.stringify(b)}</span>
            {' → '}
            <span className="text-emerald-700">{JSON.stringify(a)}</span>
          </div>
        );
      })}
    </div>
  );
};

export const AuditLogsView = ({ businessId = null }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const terms = useSectorTerms();
  const [filters, setFilters] = useState({
    from_date: '',
    to_date: '',
    actor_email: '',
    action: '',
    ip: '',
  });
  const [skip, setSkip] = useState(0);
  const limit = 50;

  const fetchActions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/superadmin/audit-logs/actions`);
      setActions(data);
    } catch {
      /* empty */
    }
  }, [API_URL]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, skip };
      if (businessId) params.business_id = businessId;
      Object.entries(filters).forEach(([k, v]) => {
        if (v) {
          if (k === 'from_date') params.from_date = `${v}T00:00:00`;
          else if (k === 'to_date') params.to_date = `${v}T23:59:59`;
          else params[k] = v;
        }
      });
      const endpoint = businessId
        ? `${API_URL}/api/superadmin/businesses/${businessId}/audit-logs?limit=${limit}`
        : `${API_URL}/api/superadmin/audit-logs`;
      const { data } = await axios.get(endpoint, { params: businessId ? {} : params });
      if (businessId) {
        setLogs(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
      } else {
        setLogs(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      toast.error('Log kaydı yüklenemedi');
    }
    setLoading(false);
  }, [API_URL, filters, skip, businessId]);

  useEffect(() => { fetchActions(); }, [fetchActions]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const exportExcel = async () => {
    try {
      const params = {};
      if (businessId) params.business_id = businessId;
      Object.entries(filters).forEach(([k, v]) => {
        if (v) {
          if (k === 'from_date') params.from_date = `${v}T00:00:00`;
          else if (k === 'to_date') params.to_date = `${v}T23:59:59`;
          else params[k] = v;
        }
      });
      const response = await axios.get(`${API_URL}/api/superadmin/audit-logs/export`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel indirildi');
    } catch {
      toast.error('Dışa aktarma başarısız');
    }
  };

  const resetFilters = () => {
    setFilters({ from_date: '', to_date: '', actor_email: '', action: '', ip: '' });
    setSkip(0);
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <div className="space-y-4" data-testid="audit-logs-view">
      {!businessId && (
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-[#2563EB]" />
          <div>
            <h2 className="text-xl font-bold text-[#1A1A2E]">Denetim Kaydı</h2>
            <p className="text-sm text-[#595959]">Tüm sistem aktivitelerinin kalıcı kaydı (90 gün / kritik olaylar süresiz)</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <Label className="text-xs">Başlangıç</Label>
          <Input type="date" value={filters.from_date} onChange={(e) => { setFilters({ ...filters, from_date: e.target.value }); setSkip(0); }} className="h-9 text-sm" data-testid="filter-from-date" />
        </div>
        <div>
          <Label className="text-xs">Bitiş</Label>
          <Input type="date" value={filters.to_date} onChange={(e) => { setFilters({ ...filters, to_date: e.target.value }); setSkip(0); }} className="h-9 text-sm" data-testid="filter-to-date" />
        </div>
        <div>
          <Label className="text-xs">Kullanıcı E-posta</Label>
          <Input value={filters.actor_email} onChange={(e) => { setFilters({ ...filters, actor_email: e.target.value }); setSkip(0); }} placeholder="örn: yildiz" className="h-9 text-sm" data-testid="filter-actor-email" />
        </div>
        <div>
          <Label className="text-xs">İşlem Tipi</Label>
          <Select value={filters.action || 'all'} onValueChange={(v) => { setFilters({ ...filters, action: v === 'all' ? '' : v }); setSkip(0); }}>
            <SelectTrigger className="h-9 text-sm" data-testid="filter-action"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {actions.map(a => (
                <SelectItem key={a} value={a}>{formatAction(a, terms).label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">IP Adresi</Label>
          <Input value={filters.ip} onChange={(e) => { setFilters({ ...filters, ip: e.target.value }); setSkip(0); }} placeholder="örn: 192.168.1.1" className="h-9 text-sm" data-testid="filter-ip" />
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#595959]">
          <strong>{total.toLocaleString('tr-TR')}</strong> kayıt
          {hasActiveFilters && (
            <button onClick={resetFilters} className="ml-2 text-[#2563EB] hover:underline text-xs" data-testid="reset-filters">
              <X className="w-3 h-3 inline" /> filtreleri temizle
            </button>
          )}
        </p>
        <Button onClick={exportExcel} variant="outline" size="sm" data-testid="export-audit-excel">
          <Download className="w-4 h-4 mr-2" /> Excel İndir
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Tarih</TableHead>
              <TableHead>İşlem</TableHead>
              <TableHead>Kullanıcı</TableHead>
              <TableHead>Hedef</TableHead>
              <TableHead>Değişiklik</TableHead>
              <TableHead className="whitespace-nowrap">IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#595959]">Yükleniyor...</TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#999]" data-testid="no-logs">
                <FileText className="w-8 h-8 mx-auto mb-2" strokeWidth={1.5} />
                <p>Kayıt bulunamadı</p>
              </TableCell></TableRow>
            ) : logs.map((log) => {
              const a = formatAction(log.action, terms);
              return (
                <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                  <TableCell className="text-xs text-[#595959] whitespace-nowrap tabular-nums">{fmtDate(log.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.color}`} data-testid={`action-${log.action}`}>
                        {a.label}
                      </span>
                      {log.critical && <AlertTriangle className="w-3 h-3 text-amber-500" title="Kritik olay — süresiz saklanır" />}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="text-[#1A1A2E] truncate max-w-[180px]" title={log.actor_email}>{log.actor_email || '-'}</div>
                    <div className="text-[10px] text-[#999]">{log.actor_role || ''}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {log.target_label || log.target_type || '-'}
                  </TableCell>
                  <TableCell>
                    <DiffCell before={log.before} after={log.after} />
                  </TableCell>
                  <TableCell className="text-xs font-mono text-[#595959]">
                    <div>{log.ip_address || '-'}</div>
                    {log.geo && (log.geo.flag || log.geo.country) && (
                      <div className="text-[10px] text-[#999] mt-0.5" data-testid={`geo-${log.id}`}>
                        {log.geo.flag || '🌐'} {log.geo.city ? `${log.geo.city}, ` : ''}{log.geo.country || ''}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!businessId && total > limit && (
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="prev-page">
            Önceki
          </Button>
          <span className="text-xs text-[#595959]">
            {skip + 1} - {Math.min(skip + limit, total)} / {total}
          </span>
          <Button variant="outline" size="sm" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} data-testid="next-page">
            Sonraki
          </Button>
        </div>
      )}
    </div>
  );
};
